import { SkyTabList, SkyText } from "@skypointcloud/skypoint-ui";
import type {
  Reliability,
  ReliabilityWindow,
  ScheduleReliability,
} from "@shared/types/reliability";
import { SKYPOINT_LOOKING_INTO_IT } from "@shared/types/whoActs";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { ReliabilityHelpLink, successRateText } from "./Freshness";

/**
 * Level B on Schedules (#828): each schedule's success rate over 7 or 30 days and how its last run
 * ended, under its Last run. A cause on Skypoint's side says so in `whoActs`'s own words.
 */

export const WINDOW_LABEL: Readonly<Record<ReliabilityWindow, string>> = {
  "7d": "7 d",
  "30d": "30 d",
};

export function scheduleReliabilityOf(
  data: Reliability | undefined,
  scheduleId: string,
): ScheduleReliability | null {
  return data?.schedules.find((s) => s.scheduleId === scheduleId) ?? null;
}

/** "Success (7 d): 80% of 5 runs", or "Success (7 d): No runs". */
export function successLine(row: ScheduleReliability, window: ReliabilityWindow): string {
  const rate = successRateText(row.successRate);
  const of = row.runs === 0 ? "" : ` of ${row.runs} ${row.runs === 1 ? "run" : "runs"}`;
  return `Success (${WINDOW_LABEL[window]}): ${rate}${of}`;
}

/** How the last run ended, and "Skypoint is looking into it" when the cause is Skypoint's. */
export function lastOutcomeLine(row: ScheduleReliability): string | null {
  if (!row.lastOutcome) return null;
  const label = runStatusPresentation(row.lastOutcome).label;
  return row.lastWhoActs === "skypoint" ? `${label}. ${SKYPOINT_LOOKING_INTO_IT}.` : label;
}

export function ScheduleReliabilityLines({
  row,
  window,
}: {
  row: ScheduleReliability | null;
  window: ReliabilityWindow;
}) {
  if (!row) return null;
  const outcome = lastOutcomeLine(row);
  return (
    <>
      {outcome ? (
        <SkyText size={200} color="muted" block testId={`schedule-last-outcome-${row.scheduleId}`}>
          {outcome}
        </SkyText>
      ) : null}
      <SkyText size={200} color="muted" block testId={`schedule-success-${row.scheduleId}`}>
        {successLine(row, window)}
      </SkyText>
    </>
  );
}

/** The 7 d or 30 d choice above the table, with the guide's explanation beside it. */
export function ReliabilityWindowToggle({
  window,
  onChange,
}: {
  window: ReliabilityWindow;
  onChange: (window: ReliabilityWindow) => void;
}) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
      <SkyText size={200} color="muted" id="schedules-success-window">
        Success over
      </SkyText>
      <SkyTabList
        ariaLabelledBy="schedules-success-window"
        testId="schedules-success-window"
        selectedValue={window}
        tabs={[
          { value: "7d", label: "7 days" },
          { value: "30d", label: "30 days" },
        ]}
        onTabSelect={(value) => onChange(value === "30d" ? "30d" : "7d")}
      />
      <ReliabilityHelpLink screen="schedules" testId="schedules-reliability-help" />
    </div>
  );
}
